import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useReveal from '../../../hooks/useReveal';
import './PostCarousel.css';

const VISIBLE_DESKTOP = 3;
const VISIBLE_MOBILE = 1;
const MOBILE_QUERY = '(max-width: 720px)';

/**
 * Generic paginated, swipeable row of cards with skillset-style arrow + dot
 * controls. Desktop shows `visibleDesktop` cards at a time, mobile shows one.
 * Extracted from the homepage blog teaser so any "latest/related X" strip
 * (blog posts, case studies, ...) can reuse the same carousel mechanics.
 */
const PostCarousel = ({
  items,
  renderItem,
  itemKey,
  title,
  subtitle,
  viewAllTo,
  viewAllLabel,
  ariaLabel,
  visibleDesktop = VISIBLE_DESKTOP,
  visibleMobile = VISIBLE_MOBILE,
}) => {
  const sectionRef = useReveal({ threshold: 0.1 });
  const trackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(visibleDesktop);

  const maxIndex = Math.max(0, items.length - visibleCount);
  const showControls = maxIndex > 0;

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const applyBreakpoint = () => {
      setVisibleCount(mq.matches ? visibleMobile : visibleDesktop);
      setActiveIndex(0);
      trackRef.current?.scrollTo({ left: 0 });
    };
    applyBreakpoint();
    mq.addEventListener('change', applyBreakpoint);
    return () => mq.removeEventListener('change', applyBreakpoint);
  }, [visibleDesktop, visibleMobile]);

  const getStep = () => {
    const track = trackRef.current;
    const card = track?.querySelector('.post-carousel-item');
    if (!track || !card) return 0;
    const gap = parseFloat(getComputedStyle(track).columnGap || 0);
    return card.getBoundingClientRect().width + gap;
  };

  const updateActiveIndex = () => {
    const track = trackRef.current;
    const step = getStep();
    if (!track || !step) return;
    const index = Math.round(track.scrollLeft / step);
    setActiveIndex(Math.min(maxIndex, Math.max(0, index)));
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    track.addEventListener('scroll', updateActiveIndex, { passive: true });
    window.addEventListener('resize', updateActiveIndex);
    return () => {
      track.removeEventListener('scroll', updateActiveIndex);
      window.removeEventListener('resize', updateActiveIndex);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxIndex]);

  const goTo = useCallback(
    (index) => {
      const track = trackRef.current;
      const step = getStep();
      if (!track || !step) return;
      const clamped = Math.min(maxIndex, Math.max(0, index));
      track.scrollTo({ left: clamped * step, behavior: 'smooth' });
      setActiveIndex(clamped);
    },
    [maxIndex]
  );

  return (
    <section className="post-carousel reveal" ref={sectionRef}>
      <div className="post-carousel-container">
        <div className="post-carousel-header">
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          {viewAllTo && (
            <Link to={viewAllTo} className="post-carousel-all">
              {viewAllLabel}
            </Link>
          )}
        </div>

        <div
          className="post-carousel-track"
          ref={trackRef}
          role="region"
          aria-roledescription="Karussell"
          aria-label={ariaLabel}
        >
          {items.map((item, index) => (
            <div
              key={itemKey(item)}
              className="post-carousel-item"
              style={{ '--card-delay': `${index * 90}ms` }}
            >
              {renderItem(item, index)}
            </div>
          ))}
        </div>

        {showControls && (
          <div className="post-carousel-controls">
            <button
              type="button"
              className="post-carousel-arrow"
              aria-label="Vorherige Einträge"
              onClick={() => goTo(activeIndex - 1)}
              disabled={activeIndex === 0}
            >
              <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                <path d="M12.79 4.21a.75.75 0 010 1.06L8.06 10l4.73 4.73a.75.75 0 11-1.06 1.06l-5.26-5.26a.75.75 0 010-1.06l5.26-5.26a.75.75 0 011.06 0z" />
              </svg>
            </button>

            <div className="post-carousel-indicators">
              {Array.from({ length: maxIndex + 1 }, (_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`post-carousel-indicator ${index === activeIndex ? 'active' : ''}`}
                  aria-label={`Einträge ${index + 1} anzeigen`}
                  aria-current={index === activeIndex}
                  onClick={() => goTo(index)}
                />
              ))}
            </div>

            <button
              type="button"
              className="post-carousel-arrow"
              aria-label="Weitere Einträge"
              onClick={() => goTo(activeIndex + 1)}
              disabled={activeIndex === maxIndex}
            >
              <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                <path d="M7.21 4.21a.75.75 0 011.06 0l5.26 5.26a.75.75 0 010 1.06l-5.26 5.26a.75.75 0 11-1.06-1.06L11.94 10 7.21 5.27a.75.75 0 010-1.06z" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default PostCarousel;
